import Asset from "../../model/Asset"
import Income, {IncomeType} from "../../model/Income"
import Model from "../../model/Model"
import type {IBudgetCalc, IIncomeItemSummary} from "./Calculator"
import {roundMoney} from "../../stores/StoreUtilities"

// Helpers for annuity asset types with a guaranteed lifetime income rider (GLWB).
// The rider is stored as a real Income record linked by Income.assetId — the income
// summary is the single source of truth for how much the rider pays in a given year,
// so the balance drawdown and RMD credit always agree with the taxed amount.

export function hasIncomeRider(asset: Asset): boolean {
  return asset.assetTypeDef?.hasIncomeRider === true
}

// The linked pension Income this feature manages for a rider asset. A generic Income.assetId link predates the
// feature, so it is not enough on its own: the linked asset must be a rider type.
export function isManagedRiderIncome(model: Model, income: Income): boolean {
  const asset = income.assetId ? model.getAssetById(income.assetId) : undefined
  return !!asset && hasIncomeRider(asset) &&
    (income.incomeType === IncomeType.PensionsAndAnnuitiesTaxable || income.incomeType === IncomeType.PensionsAndAnnuitiesNonTaxable)
}

// Every managed rider Income for an asset, archived ones included (archive, unarchive and repair act on them).
export function managedRiderIncomes(model: Model, asset: Asset): Income[] {
  return hasIncomeRider(asset) ? model.incomes.filter((i: Income) => i.assetId === asset.id && isManagedRiderIncome(model, i)) : []
}

// The id of a rider Income this feature creates. It derives from the asset, so a create retried after an
// ambiguous result finds the first record instead of making a second one.
export function riderIncomeId(asset: Asset): string {
  return `${asset.id}-rider`
}

// What is wrong with a rider asset and its managed Income, or undefined for a valid pair. A fully archived pair is
// dormant, not an error.
export function riderPairProblem(model: Model, asset: Asset): string | undefined {
  if (!hasIncomeRider(asset)) return undefined
  const active = managedRiderIncomes(model, asset).filter((i: Income) => !i.archived)
  if (asset.archived) return active.length > 0 ? "has active rider income while the asset is archived" : undefined
  if (active.length === 0) return "is missing its rider income"
  if (active.length > 1) return `has ${active.length} rider incomes`
  if (active[0].ownerId !== asset.ownerId) return "has rider income for a different owner"
  if (active[0].incomeType !== riderIncomeType(asset)) return "has rider income taxed for a different account type"
  return undefined
}

// Projections refuse to run on an invalid pair rather than projecting it as something it isn't
export function riderPairError(model: Model): string | undefined {
  for (const asset of model.assets) {
    const problem = riderPairProblem(model, asset)
    if (problem) return `${asset.description} ${problem}. Open it on the Assets & Liabilities page to repair it.`
  }
  return undefined
}

export function riderIncomes(model: Model, asset: Asset): Income[] {
  if (!hasIncomeRider(asset)) {
    return []
  }
  return model.incomes.filter((i: Income) => !i.archived && i.assetId === asset.id &&
    (i.incomeType === IncomeType.PensionsAndAnnuitiesTaxable || i.incomeType === IncomeType.PensionsAndAnnuitiesNonTaxable))
}

export interface IRiderIncomeYear {
  fullYear: number    // survivor-adjusted amount the rider pays this calc year
  remaining: number   // post-snapshot portion (first year only; equals fullYear otherwise)
}

export function riderIncomeForYear(asset: Asset, model: Model, budgetCalc?: IBudgetCalc, proRateBudget?: IBudgetCalc): IRiderIncomeYear {
  const incomes = riderIncomes(model, asset)
  if (incomes.length === 0 || !budgetCalc) {
    return {fullYear: 0, remaining: 0}
  }
  let fullYear = 0
  let remaining = 0
  incomes.forEach((income: Income) => {
    const item = budgetCalc.incomeSummary.byItem.find((it: IIncomeItemSummary) => it.id === income.id)
    fullYear += item?.total ?? 0
    if (proRateBudget) {
      const proRateItem = proRateBudget.incomeSummary.byItem.find((it: IIncomeItemSummary) => it.id === income.id)
      remaining += proRateItem?.total ?? 0
    }
  })
  if (!proRateBudget) {
    remaining = fullYear
  }
  // Twelve exact monthly payments sum to e.g. 1999.9999999999998; a sub-cent remainder would
  // otherwise leave a phantom RMD residual
  return {fullYear: roundMoney(fullYear), remaining: roundMoney(remaining)}
}

// A contract is in its deferral period while its linked rider income starts in a later year.
// A rider that stopped, or whose single-life owner died before it could start, is neither
// active nor deferred (endYear 0 means no end date).
export function isRiderDeferred(asset: Asset, model: Model, year: number): boolean {
  return riderIncomes(model, asset).some((i: Income) => !!i.startDate && i.startDate.getFullYear() > year &&
    (i.endYear === 0 || i.endYear >= year))
}

// A rider is "active" in a year only when its linked income actually pays that year.
// This lifts the withdrawal exclusion once a single-life rider stops at the owner's
// death, and stays true after the contract value depletes to $0 (the income record
// pays independently of the balance).
export function isRiderActive(asset: Asset, model: Model, budgetCalc?: IBudgetCalc): boolean {
  return riderIncomeForYear(asset, model, budgetCalc).fullYear > 0
}

// The rider is stored as a monthly income. Never round the payment to whole dollars:
// $2,000/yr would become $167 × 12 = $2,004/yr.
export function riderMonthlyPayment(annualIncome: number): number {
  return annualIncome / 12
}

export function riderIncomeType(asset: Asset): IncomeType {
  return asset.assetTypeDef?.taxFree ? IncomeType.PensionsAndAnnuitiesNonTaxable : IncomeType.PensionsAndAnnuitiesTaxable
}
